<template>
    <div class="w-full h-full flex flex-col">
        <VAceEditor ref="editorRef" class="w-full flex-grow border custom-ace" v-model:value="localEditedText"
            lang="yaml" :theme="editorStore.theme" :options="editorOptions" @change="onEditorChange" />
        <!-- Button (Separate, No Scrollbars) -->
        <div class="mt-4 flex items-center space-x-2">
            <form-button v-if="props.settings.tauri" class="w-1/4" :disabled="isDeploying" @click="deployFilename = settings.plate.name + '.yaml'; showDeployConfirm = true">
                Deploy to HA
            </form-button>
            <form-button v-if="props.settings.tauri" class="w-1/4" :disabled="isReloading" @click="showReloadConfirm = true">
                {{ isReloading ? 'Reloading...' : 'Reload HA' }}
            </form-button>
            <form-button v-if="props.settings.tauri" class="w-1/4" :disabled="isLoadingFromHa" @click="loadFromHa">
                {{ isLoadingFromHa ? 'Loading...' : 'Load from HA' }}
            </form-button>
            <form-button :class="props.settings.tauri ? 'w-1/4' : 'w-full'" @click="openModal">
                Save to file
            </form-button>
            <AceThemeSelector class="ml-auto flex-shrink-0" />
        </div>
        <!-- Reload confirmation balloon -->
        <div v-if="showReloadConfirm" class="mt-2 bg-white/10 border border-white/20 rounded shadow p-3">
            <p class="text-sm font-medium text-white">Reload openHASP on {{ settings.hass.host }}?</p>
            <div class="flex space-x-2 mt-2">
                <form-button color="danger" class="w-16" @click="showReloadConfirm = false; reloadHass()">Yes</form-button>
                <form-button class="w-16" @click="showReloadConfirm = false">No</form-button>
            </div>
        </div>
        <!-- Deploy confirmation balloon -->
        <div v-if="showDeployConfirm" class="mt-2 bg-white/10 border border-white/20 rounded shadow p-3">
            <p class="text-sm font-medium text-white">Deploy <strong>{{ deployFilename }}</strong> to {{ settings.hass.host }} via SFTP?</p>
            <div class="flex space-x-2 mt-2">
                <form-button color="danger" class="w-16" @click="confirmDeploy">Yes</form-button>
                <form-button class="w-16" @click="showDeployConfirm = false">No</form-button>
            </div>
        </div>
        <!-- Deploy progress -->
        <div v-if="isDeploying" class="mt-2">
            <div class="flex justify-between text-xs text-green-600 mb-1">
                <span>{{ deployStatus }}</span>
            </div>
            <div class="w-full bg-white/15 rounded-full h-2">
                <div class="bg-green-500 h-2 rounded-full animate-pulse w-full" />
            </div>
        </div>
        <!-- Deploy status message -->
        <div v-if="deployStatus && !isDeploying" class="mt-2 text-xs px-2 py-1 rounded"
            :class="deployStatus.startsWith('Deploy failed') || deployStatus.startsWith('Upload succeeded')
                ? 'bg-white/10 text-red-400'
                : 'bg-white/10 text-green-400'">
            {{ deployStatus }}
        </div>
        <!-- Reload status message -->
        <div v-if="reloadStatus" class="mt-2 text-xs px-2 py-1 rounded"
            :class="reloadStatus.startsWith('Failed') ? 'bg-white/10 text-red-400' : 'bg-white/10 text-green-400'">
            {{ reloadStatus }}
        </div>
        <!-- Load from HA status message -->
        <div v-if="loadFromHaStatus" class="mt-2 text-xs px-2 py-1 rounded"
            :class="loadFromHaStatus.startsWith('Failed') ? 'bg-white/10 text-red-400' : 'bg-white/10 text-green-400'">
            {{ loadFromHaStatus }}
        </div>
        <!-- Modal to get the filename -->
        <FilePrompt v-model:filenamePreset="userFilename" @save="saveToFile" :visible="isModalVisible"
            @update:visible="isModalVisible = false" />
    </div>
</template>

<script setup>
import { ref, watchEffect, watch } from "vue";
import { debounce } from "lodash";
import { VAceEditor } from 'vue3-ace-editor';
import FilePrompt from './FilePrompt.vue';
import AceThemeSelector from '../AceThemeSelector.vue';
import { useEditorStore } from '../../stores/editorStore';
import { useUndoStore } from '../../history/undoStore';
import 'ace-builds/src-noconflict/mode-yaml';
import '/src/ace-config';
import Exporter from "../../exporter/Exporter";
import { invoke } from '@tauri-apps/api/core';
import {
    deployOpenHaspPlateYaml,
    reloadOpenHasp,
} from '../../services/homeAssistantService.js';

const editorStore = useEditorStore();
const undoStore = useUndoStore();

// Props
const props = defineProps({
    popupState: Boolean, // Detect when tab opens
    settings: {
        type: Object,
        required: true,
    },
    layer: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    hassConfigUpdate: {
        type: Number,
        required: true,
    },
});

// Reactive Data
const editorRef = ref(null);
const localEditedText = ref("");
const isModalVisible = ref(false);
const editorOptions = ref({
    fontSize: 12,
    // useWorker: true,
});

// ace.config.setModuleUrl('ace/mode/json_worker', workerJsonUrl);
const userFilename = ref(props.settings.plate.name);

const updateEditor = debounce(() => {
    const exporter = new Exporter();
    exporter
        .hassExport(props.settings, props.layer)
        .then((result) => {
            localEditedText.value = result;
        })
        .catch((error) => {
            localEditedText.value = `Error: ${error.message}`;
        });
}, 100); // Adjust the debounce delay as necessary

// Watch for popup open and preload AceEditor text
watchEffect(() => {
    if (props.popupState) {
        updateEditor();
    }
});


// Watch for hassConfigUpdate changes
watch(() => props.hassConfigUpdate, () => {
    updateEditor();
});

// Logic/HA flow edits and undo/redo bypass layerCallback; history catches them.
watch(() => undoStore.historyVersion, updateEditor);

// Handle the editor text change
const onEditorChange = (newValue) => {
    const editor = editorRef.value?.editor; // Check if the editor is available
    if (!editor) return; // Ensure the editor is available before proceeding
    const session = editor.getSession();
    const changedLines = [];

    // Compare the new content with the previous text to detect changed lines
    const diff = compareText(previousText.value, newValue);

    diff.forEach((lineNumber) => {
        changedLines.push(lineNumber);
        // You could add annotation or styling to the gutter for changed lines
        session.addGutterDecoration(lineNumber, 'changed-line');
    });

    previousText.value = newValue; // Update the previous text for next comparison
};

// Function to compare the previous and new text to determine changed lines
const compareText = (oldText, newText) => {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    const changedLines = [];

    // Compare line by line
    for (let i = 0; i < Math.max(oldLines.length, newLines.length); i++) {
        if (oldLines[i] !== newLines[i]) {
            changedLines.push(i); // Line number of change
        }
    }
    return changedLines;
};

const showDeployConfirm = ref(false);
const isDeploying = ref(false);
const deployStatus = ref('');
const deployFilename = ref('');

const showReloadConfirm = ref(false);
const isReloading = ref(false);
const reloadStatus = ref('');

const isLoadingFromHa = ref(false);
const loadFromHaStatus = ref('');

const confirmDeploy = () => {
    showDeployConfirm.value = false;
    deployToHass();
};

const deployToHass = async () => {
    const filename = deployFilename.value;
    isDeploying.value = true;
    deployStatus.value = 'Uploading via SFTP...';

    try {
        try {
            const result = await deployOpenHaspPlateYaml(props.settings, filename, localEditedText.value);
            const count = Array.isArray(result.reloaded) ? ` and reloaded ${result.reloaded.length} openHASP integration(s)` : '';
            deployStatus.value = `Deployed ${filename}${count}.`;
        } catch (error) {
            if (error?.uploaded) {
                console.error('Plate YAML uploaded, but openHASP reload failed:', error);
                deployStatus.value = `Upload succeeded, but reloading openHASP failed: ${error?.message || error}`;
            } else {
                throw error;
            }
        }
    } catch (error) {
        console.error('SFTP deploy failed:', error);
        deployStatus.value = `Deploy failed: ${error}`;
    } finally {
        isDeploying.value = false;
    }
};

const reloadHass = async () => {
    isReloading.value = true;
    reloadStatus.value = '';
    try {
        const reloaded = await reloadOpenHasp(props.settings);
        reloadStatus.value = Array.isArray(reloaded)
            ? `Reloaded ${reloaded.length} openHASP integration(s).`
            : 'openHASP reloaded successfully.';
    } catch (error) {
        reloadStatus.value = `Failed to reload: ${error}`;
    } finally {
        isReloading.value = false;
    }
};

const loadFromHa = async () => {
    const filename = props.settings.plate.name + '.yaml';
    isLoadingFromHa.value = true;
    loadFromHaStatus.value = '';
    try {
        const base64data = await invoke('sftp_download_file_command', {
            connArgs: {
                host: props.settings.hass.host,
                port: Number(props.settings.hass.sftp_port),
                user: props.settings.hass.sftp_user,
                password: props.settings.hass.sftp_pass,
                timeout: 15,
            },
            remotePath: props.settings.hass.sftp_path,
            filename,
        });
        localEditedText.value = new TextDecoder().decode(
            Uint8Array.from(atob(base64data), c => c.charCodeAt(0))
        );
        loadFromHaStatus.value = `Loaded ${filename} from ${props.settings.hass.host}`;
    } catch (error) {
        loadFromHaStatus.value = `Failed to load: ${error}`;
    } finally {
        isLoadingFromHa.value = false;
    }
};

const openModal = () => {
    isModalVisible.value = true;
};

const saveToFile = (filename) => {
    if (!props.settings?.plate?.name) {
        console.error("Plate name is missing");
        return;
    }

    const now = new Date();
    const timestamp = now.toISOString().slice(2, 19).replace(/[-T:]/g, "");

    // Use user-defined filename or default to plate name + timestamp
    const userFilename = filename ? `${filename}.yaml` : `${props.settings.plate.name}-${timestamp}.yaml`;

    const blob = new Blob([localEditedText.value], { type: "application/yaml" });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = userFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};


</script>

<style scoped>
/* Add a style to highlight changed lines in the gutter */
.ace_changed-line {
    background-color: rgba(255, 0, 0, 0.3);
}

.custom-ace .ace_content {
    background-color: gray !important;
}
</style>
